import React from 'react'
import {
	Card,
	Count,
	DeletedTag,
	Head,
	Label,
	List,
	RowLink,
	RowMeta,
	RowPrimary,
	RowSecondary,
	RowText,
	SeeAll,
} from './StatCard.styles'
import { EmptyNoteInline } from '@components/EmptyState'

/** One recent-activity row inside a stat card. */
export interface StatCardRow {
	id: number
	/** Route the row links to (the resource's detail page). */
	to: string
	primary: string
	secondary?: string
	/** Right-aligned meta (e.g. a date or amount). */
	meta?: string
	deleted?: boolean
}

interface StatCardProps {
	label: string
	count: number
	/** Route the "See all" footer links to (the resource's list page). */
	seeAllTo: string
	rows: StatCardRow[]
	emptyText: string
}

/**
 * One overview card: a big count + label, the most-recent rows (each links to its
 * detail page), and a "See all →" footer linking to the full list. Counts and rows
 * are already scoped to the admin date-range filter by the caller.
 */
const StatCard: React.FC<StatCardProps> = ({ label, count, seeAllTo, rows, emptyText }) => {
	return (
		<Card>
			<Head>
				<Count>{count.toLocaleString()}</Count>
				<Label>{label}</Label>
			</Head>

			<List>
				{rows.length === 0 ? (
					<EmptyNoteInline>{emptyText}</EmptyNoteInline>
				) : (
					rows.map(row => (
						<RowLink key={row.id} to={row.to} $deleted={row.deleted}>
							<RowText>
								<RowPrimary>
									{row.primary}
									{row.deleted && <DeletedTag>Deleted</DeletedTag>}
								</RowPrimary>
								{row.secondary && <RowSecondary>{row.secondary}</RowSecondary>}
							</RowText>
							{row.meta && <RowMeta>{row.meta}</RowMeta>}
						</RowLink>
					))
				)}
			</List>

			<SeeAll to={seeAllTo}>See all →</SeeAll>
		</Card>
	)
}

export default StatCard
